import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { DsToastModel, DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Subject } from 'rxjs';
import { BaseTone } from 'src/app/shared/enums/tone.enum';
import {
  DealerSettings,
  DealerWifi,
  OutletSettings,
  PdiMarketSettings,
} from 'src/app/shared/models/dealer-settings.model';

import { DealerService } from '../services/dealer.service';
import { DealerStore } from './dealer.store';

@Injectable()
export class DealerSettingsStore {
  private readonly dealerStore = inject(DealerStore);
  private readonly dealerService = inject(DealerService);
  private readonly toastService = inject(DsToastService);
  private readonly translationService = inject(TranslateService);

  private _dealerSettings = signal<DealerSettings | undefined>(undefined);

  private _isLoading = signal<boolean>(true);
  readonly isLoading = this._isLoading.asReadonly();

  private _loadingWifiChanges = signal<boolean>(false);
  readonly loadingWifiChanges = this._loadingWifiChanges.asReadonly();

  private _loadingOutletSettings = signal<boolean>(false);
  readonly loadingOutletSettingsChanges = this._loadingOutletSettings.asReadonly();

  private _loadingPdiSettings = signal<boolean>(false);
  readonly loadingPdiSettingsChanges = this._loadingPdiSettings.asReadonly();

  readonly wifiList = computed(() => this._dealerSettings()?.wifiList || []);
  readonly outletSettings = computed(() => this._dealerSettings()?.outletSettings || undefined);
  readonly pdiMarketSettings = computed(() => this._dealerSettings()?.pdiMarketSettings || []);
  readonly defaultWifiId = computed(() => {
    return this._dealerSettings()?.wifiList?.find((w) => w.isDefault)?.id || this._dealerSettings()?.wifiList[0]?.id;
  });
  readonly defaultWifiSSID = computed(
    () => this._dealerSettings()?.wifiList.find((w) => w.isDefault)?.ssid || this._dealerSettings()?.wifiList[0]?.ssid
  );
  readonly getDownloadCapacity = computed(() => this._dealerSettings()?.outletSettings?.downloadCapacity || 2);

  readonly outletSettingsUpdateError = new Subject<void>();
  readonly pdiSettingsUpdateError = new Subject<void>();

  constructor() {
    effect(() => {
      const dealer = this.dealerStore.selectedDealer();
      if (dealer) {
        this.requestDealerSettings();
      }
    });
  }

  createWifi(wifi: DealerWifi): void {
    this._loadingWifiChanges.set(true);

    if (wifi.isPreConfigured) {
      this.createPreConfiguredWifi(wifi);
    } else {
      this.createRegularWifi(wifi);
    }
  }

  createPreConfiguredWifi(wifi: DealerWifi): void {
    this.dealerService
      .createWifi(this.dealerStore.selectedDealer()!.bpId, wifi)
      .pipe(finalize(() => this._loadingWifiChanges.set(false)))
      .subscribe({
        next: (wifi) => {
          const wifiList = this.addWifiToList(wifi);
          this.patchSignal(this._dealerSettings, this._dealerSettings(), {
            wifiList,
          });
          this.handleSuccess('settings.addPreconfiguredWifiSuccess');
        },
        error: (err: HttpErrorResponse) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('default-settings-create-pre-configured-wifi-error-toast', BaseTone.CRITICAL, errorMessage);
          } else if (err.status === 400) {
            this.handleInfoMessage('settings.duplicatedWifi');
          } else if (err.status === 404) {
            this.handleErrorMessage('settings.noSetupCodeMatch');
          } else {
            this.handleErrorMessage('settings.addPreconfiguredWifiError');
          }
        },
      });
  }

  createRegularWifi(wifi: DealerWifi): void {
    this.dealerService
      .createWifi(this.dealerStore.selectedDealer()!.bpId, wifi)
      .pipe(finalize(() => this._loadingWifiChanges.set(false)))
      .subscribe({
        next: (wifi) => {
          const wifiList = this.addWifiToList(wifi);
          this.patchSignal(this._dealerSettings, this._dealerSettings(), {
            wifiList,
          });
          this.handleSuccess();
        },
        error: (err: HttpErrorResponse) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('default-settings-create-wifi-error-toast', BaseTone.CRITICAL, errorMessage);
          } else if (err.status === 400) {
            this.handleInfoMessage('settings.duplicatedWifi');
          } else {
            this.handleError();
          }
        },
      });
  }

  updateWifi(wifi: DealerWifi): void {
    this._loadingWifiChanges.set(true);
    this.dealerService
      .updateWifi(this.dealerStore.selectedDealer()!.bpId, wifi)
      .pipe(finalize(() => this._loadingWifiChanges.set(false)))
      .subscribe({
        next: (wifi) => {
          const wifiList = this.updateWifiFromList(wifi);
          this.patchSignal(this._dealerSettings, this._dealerSettings(), {
            wifiList,
          });
          this.handleSuccess();
        },
        error: (err: HttpErrorResponse) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('default-settings-update-wifi-error-toast', BaseTone.CRITICAL, errorMessage);
          } else if (err.status === 400) {
            this.handleInfoMessage('settings.duplicatedWifi');
          } else {
            this.handleError();
          }
        },
      });
  }

  updateDefaultWifi(wifi: DealerWifi): void {
    this._loadingWifiChanges.set(true);
    this.dealerService
      .updateDefaultWifi(this.dealerStore.selectedDealer()!.bpId, wifi)
      .pipe(finalize(() => this._loadingWifiChanges.set(false)))
      .subscribe({
        next: () => {
          const wifiList = this.updateDefaultWifiFromList(wifi);
          this.patchSignal(this._dealerSettings, this._dealerSettings(), {
            wifiList,
          });
          this.handleSuccess();
        },
        error: () => this.handleError(),
      });
  }

  forgetWifi(wifi: DealerWifi): void {
    this._loadingWifiChanges.set(true);
    this.dealerService
      .deleteWifi(this.dealerStore.selectedDealer()!.bpId, wifi)
      .pipe(finalize(() => this._loadingWifiChanges.set(false)))
      .subscribe({
        next: () => {
          const wifiList = this.removeWifiFromList(wifi);
          this.patchSignal(this._dealerSettings, this._dealerSettings(), {
            wifiList,
          });
          this.handleSuccess();
        },
        error: (err) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('default-settings-delete-wifi-error-toast', BaseTone.CRITICAL, errorMessage);
          }
          this.handleErrorMessage(err.error.message);
        },
      });
  }

  updateOutletSettings(outletSettings: OutletSettings): void {
    this._loadingOutletSettings.set(true);
    this.dealerService
      .updateOutletSettings(this.dealerStore.selectedDealer()!.bpId, outletSettings)
      .pipe(finalize(() => this._loadingOutletSettings.set(false)))
      .subscribe({
        next: (outletSettings) => {
          this.patchSignal(this._dealerSettings, this._dealerSettings(), {
            outletSettings,
          });
          this.handleSuccess();
        },
        error: (err) => {
          this.outletSettingsUpdateError.next();
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('default-settings-update-outlet-settings-error-toast', BaseTone.CRITICAL, errorMessage);
          } else {
            return this.handleError();
          }
        },
      });
  }

  updatePdiSettings(newField: boolean, pdiMarketSettings: PdiMarketSettings[]): void {
    this._loadingPdiSettings.set(true);

    if (newField) {
      this.createPdiMarketSettings(pdiMarketSettings);
    } else {
      this.updatePdiMarketSettings(pdiMarketSettings);
    }
  }

  createPdiMarketSettings(pdiMarketSettings: PdiMarketSettings[]): void {
    this._loadingPdiSettings.set(true);

    if (pdiMarketSettings.length !== 0) {
      this.dealerService
        .createPDIMarketSettings(
          this.dealerStore.getSelectedDealerBpId(),
          this.dealerStore.getSelectedDealerMarketCode(),
          pdiMarketSettings
        )
        .pipe(finalize(() => this._loadingPdiSettings.set(false)))
        .subscribe({
          next: () => {
            this.handleSuccess('settings.pdi.savingSuccess');
          },
          error: (err) => {
            if (err.error.code) {
              const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
              this.sendToast('default-settings-create-pdi-market-error-toast', BaseTone.CRITICAL, errorMessage);
            } else {
              return this.handleError();
            }
          },
        });
    }
  }

  updatePdiMarketSettings(pdiMarketSettings: PdiMarketSettings[]): void {
    this._loadingPdiSettings.set(true);

    if (pdiMarketSettings.length !== 0) {
      this.dealerService
        .updatePDIMarketSettings(
          this.dealerStore.getSelectedDealerBpId(),
          this.dealerStore.getSelectedDealerMarketCode(),
          pdiMarketSettings
        )
        .pipe(finalize(() => this._loadingPdiSettings.set(false)))
        .subscribe({
          next: () => {
            this.handleSuccess('settings.pdi.savingSuccess');
          },
          error: (err) => {
            if (err.error.code) {
              const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
              this.sendToast('default-settings-update-pdi-market-error-toast', BaseTone.CRITICAL, errorMessage);
            } else {
              return this.handleError();
            }
          },
        });
    }
  }

  requestDealerSettings(): void {
    const bpId = this.dealerStore.getSelectedDealerBpId();
    const marketCode = this.dealerStore.getSelectedDealerMarketCode();

    this._isLoading.set(true);
    this.dealerService
      .getDealerSettings(bpId, marketCode)
      .pipe(finalize(() => this._isLoading.set(false)))
      .subscribe({
        next: (dealerSettings) => this._dealerSettings.set(dealerSettings),
        error: (err) => {
          if (err.error.code) {
            const errorMessage = this.translationService.instant('dashboard.errors.' + err.error.code);
            this.sendToast('default-settings-request-settings-error-toast', BaseTone.CRITICAL, errorMessage);
          }
          this._dealerSettings.set({ wifiList: [] });
        },
      });
  }

  private addWifiToList(wifi: DealerWifi): DealerWifi[] {
    return [...this._dealerSettings()!.wifiList, wifi];
  }

  private updateWifiFromList(wifi: DealerWifi): DealerWifi[] {
    return this._dealerSettings()!.wifiList.map((w) => (w.id === wifi.id ? wifi : w));
  }

  private updateDefaultWifiFromList(wifi: DealerWifi): DealerWifi[] {
    const wifiList = this._dealerSettings()!.wifiList;

    wifiList.forEach((w) => {
      w.isDefault = w.id === wifi.id;
    });
    return wifiList;
  }

  private removeWifiFromList(wifi: DealerWifi): DealerWifi[] {
    const wifiList = this._dealerSettings()!.wifiList.filter((w) => w.id !== wifi.id);

    if (wifi.isDefault && wifiList.length > 0) {
      wifiList[0].isDefault = true;
    }

    return wifiList;
  }

  private handleSuccess(message = ''): void {
    const messageKey = message ? message : 'settings.savingSuccess';
    const successMessage = this.translationService.instant(messageKey);
    this.sendToast('default-settings-update-success-toast', BaseTone.POSITIVE, successMessage);
  }

  private handleError(): void {
    const errorMessage = this.translationService.instant('settings.savingError');
    this.sendToast('default-settings-update-error-toast', BaseTone.CRITICAL, errorMessage);
  }

  private handleErrorMessage(message: string): void {
    if (!message) {
      this.handleError();
    }
    const errorMessage = this.translationService.instant(message);
    this.sendToast('default-settings-update-error-toast', BaseTone.CRITICAL, errorMessage);
  }

  private handleInfoMessage(message: string): void {
    if (!message) {
      this.handleError();
    }
    const errorMessage = this.translationService.instant(message);
    this.sendToast('default-settings-update-error-toast', BaseTone.INFO, errorMessage);
  }

  private sendToast(id: string, tone: DsToastModel['tone'], toastText: string): void {
    this.toastService.pushToast({
      id,
      tone,
      toastText,
    } as DsToastModel);
  }

  private patchSignal<T>(signal: WritableSignal<T>, state: T, partialState: Partial<T>): void {
    signal.update(() => ({
      ...state,
      ...partialState,
    }));
  }
}
